import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import { isRequired } from '@gef-ui/form-core/validations/validationFunctions/common';

import {
	isDateWithOrder,
	isFullySpecifiedDate,
	isPositiveNumber,
	isPositivePercentage,
} from 'app/forms/validations/common';
import isCampaignName from 'app/forms/validations/isCampaignName';
import { campaignTypes } from 'constants/campaigns';
import { KMP_NEW_CAMPAIGN } from 'features/common/acl/constants';
import { COMMON_FORM_FIELD_NAMES } from 'features/common/forms/constants';
import { getDetail } from 'features/views/campaignSettings/campaignDetail/selectors';

export const CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME = 'campaignSettings-campaign-form';

const { campaignName, channel, communicationName, campaignType, startDate, endDate } = COMMON_FORM_FIELD_NAMES;

export const FIELDS = {
	// outputs only
	status: 'status',
	campaignCode: 'campaignCode',
	communicationCode: 'communicationCode',
	contactCount: 'contactCount',

	// possibly editable fields
	campaignName,
	startDate,
	endDate,
	communicationName,
	channel,
	campaignType,

	// editable fields
	expectedReplacement: 'expectedReplacement',
	productionPlan: 'productionPlan',
	expectedValue: 'expectedValue',
	letterPrintedDate: 'letterPrintedDate',
	addMatOrder: 'addMatOrder',
	nominationDate: 'nominationDate',
	orderStartDate: 'orderStartDate',
	orderEndDate: 'orderEndDate',
	openLetterDate: 'openLetterDate',
	description: 'description',
};

export default () => ({
	[FIELDS.campaignName]: {
		deepPath: [FIELDS.campaignName],
		validations: [
			isRequired({
				when: ({ reduxState }) => hasPermissionByIds(reduxState)(KMP_NEW_CAMPAIGN),
			}),
			isCampaignName(),
		],
	},
	[FIELDS.channel]: {
		deepPath: [FIELDS.channel],
		validations: [
			isRequired({
				when: ({ reduxState }) => hasPermissionByIds(reduxState)(KMP_NEW_CAMPAIGN),
			}),
		],
	},
	[FIELDS.communicationName]: {
		deepPath: [FIELDS.communicationName],
		validations: [
			isRequired({
				when: ({ reduxState }) => hasPermissionByIds(reduxState)(KMP_NEW_CAMPAIGN),
			}),
			isCampaignName(),
		],
	},
	[FIELDS.campaignType]: {
		deepPath: [FIELDS.campaignType],
		validations: [
			isRequired({
				when: ({ reduxState }) => hasPermissionByIds(reduxState)(KMP_NEW_CAMPAIGN),
			}),
		],
	},
	[FIELDS.startDate]: {
		deepPath: [FIELDS.startDate],
		dependentFields: [FIELDS.endDate],
		validations: [
			isRequired({
				when: ({ reduxState }) => hasPermissionByIds(reduxState)(KMP_NEW_CAMPAIGN),
			}),
			isFullySpecifiedDate({
				when: ({ reduxState }) => hasPermissionByIds(reduxState)(KMP_NEW_CAMPAIGN),
			}),
			isDateWithOrder({ fromFieldName: FIELDS.startDate, toFieldName: FIELDS.endDate }),
		],
	},
	[FIELDS.endDate]: {
		deepPath: [FIELDS.endDate],
		dependentFields: [FIELDS.startDate],
		validations: [
			isRequired({
				when: ({ reduxState }) => hasPermissionByIds(reduxState)(KMP_NEW_CAMPAIGN),
			}),
			isFullySpecifiedDate({
				when: ({ reduxState }) => hasPermissionByIds(reduxState)(KMP_NEW_CAMPAIGN),
			}),
			isDateWithOrder({ fromFieldName: FIELDS.startDate, toFieldName: FIELDS.endDate }),
		],
	},

	[FIELDS.expectedReplacement]: {
		deepPath: [FIELDS.expectedReplacement],
		validations: [
			isPositivePercentage({
				when: ({ reduxState }) => getDetail(reduxState)?.type === campaignTypes.UP_SELL,
			}),
		],
	},
	[FIELDS.productionPlan]: {
		deepPath: [FIELDS.productionPlan],
		validations: [
			isPositiveNumber({
				when: ({ reduxState }) => getDetail(reduxState)?.type === campaignTypes.UP_SELL,
			}),
		],
	},
	[FIELDS.expectedValue]: {
		deepPath: [FIELDS.expectedValue],
		validations: [
			isPositiveNumber({
				when: ({ reduxState }) => getDetail(reduxState)?.type === campaignTypes.X_SELL,
			}),
		],
	},
	[FIELDS.letterPrintedDate]: {
		deepPath: [FIELDS.letterPrintedDate],
		validations: [],
		dependentFields: [FIELDS.openLetterDate],
	},
	[FIELDS.addMatOrder]: {
		deepPath: [FIELDS.addMatOrder],
		validations: [],
		dependentFields: [FIELDS.orderEndDate, FIELDS.orderStartDate],
	},
	[FIELDS.nominationDate]: {
		deepPath: [FIELDS.nominationDate],
		validations: [],
	},
	[FIELDS.openLetterDate]: {
		deepPath: [FIELDS.openLetterDate],
		validations: [
			isRequired({
				when: ({ formData, reduxState }) =>
					formData?.letterPrintedDate && getDetail(reduxState)?.type === campaignTypes.X_SELL,
			}),
			isFullySpecifiedDate({
				when: ({ formData, reduxState }) =>
					formData?.letterPrintedDate && getDetail(reduxState)?.type === campaignTypes.X_SELL,
			}),
		],
	},
	[FIELDS.orderStartDate]: {
		deepPath: [FIELDS.orderStartDate],
		dependentFields: [FIELDS.orderEndDate],
		validations: [
			isRequired({
				when: ({ formData }) => formData?.addMatOrder,
			}),
			isFullySpecifiedDate({
				when: ({ formData }) => formData?.addMatOrder,
			}),
			isDateWithOrder({ fromFieldName: FIELDS.orderStartDate, toFieldName: FIELDS.orderEndDate }),
		],
	},
	[FIELDS.orderEndDate]: {
		deepPath: [FIELDS.orderEndDate],
		dependentFields: [FIELDS.orderStartDate],
		validations: [
			isRequired({
				when: ({ formData }) => formData?.addMatOrder,
			}),
			isFullySpecifiedDate({
				when: ({ formData }) => formData?.addMatOrder,
			}),
			isDateWithOrder({ fromFieldName: FIELDS.orderStartDate, toFieldName: FIELDS.orderEndDate }),
		],
	},
	[FIELDS.description]: {
		deepPath: [FIELDS.description],
		validations: [],
	},
});
